import { useState } from "react";
import axios from "axios";
import { X, Save, Trash2, Star, Youtube, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoEditor({ video, token, onClose, onSaved }) {
  const isNew = !video?.id;
  const [form, setForm] = useState({
    title: video?.title || "",
    youtube_id: video?.youtube_id || "",
    description: video?.description || "",
    is_featured: !!video?.is_featured,
    order: video?.order ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Accept full YouTube URLs and extract the 11-char ID
  const handleYouTubeId = (raw) => {
    const v = raw.trim();
    const re = /(?:v=|youtu\.be\/|\/embed\/|\/shorts\/)([\w-]{11})/;
    const m = v.match(re);
    update("youtube_id", m ? m[1] : v);
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setError("");
    setSaving(true);
    const headers = { "X-Admin-Token": token };
    try {
      if (isNew) {
        const r = await axios.post(`${API}/admin/videos`, form, { headers });
        onSaved?.(r.data, "created");
      } else {
        const r = await axios.put(`${API}/admin/videos/${video.id}`, form, { headers });
        onSaved?.(r.data, "updated");
      }
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally { setSaving(false); }
  };

  const del = async () => {
    try {
      await axios.delete(`${API}/admin/videos/${video.id}`, { headers: { "X-Admin-Token": token } });
      onSaved?.(video, "deleted");
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const ytThumb = form.youtube_id ? `https://img.youtube.com/vi/${form.youtube_id}/hqdefault.jpg` : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="video-editor">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">{isNew ? "Add Video" : "Edit Video"}</div>
            <h2 className="font-display text-xl text-navy-deep mt-0.5">{form.title || "Untitled video"}</h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="video-editor-close" className="w-10 h-10 rounded-full hover:bg-cream-100 flex items-center justify-center">
            <X size={20} className="text-navy" />
          </button>
        </div>

        <form onSubmit={save} className="px-6 py-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-navy mb-1.5">Title</label>
            <input data-testid="video-title" required value={form.title} onChange={(e) => update("title", e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy mb-1.5">YouTube video ID or URL</label>
            <div className="relative">
              <Youtube size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-red-600" />
              <input data-testid="video-youtube-id" required value={form.youtube_id}
                onChange={(e) => handleYouTubeId(e.target.value)}
                placeholder="e.g. dQw4w9WgXcQ  or  https://youtu.be/dQw4w9WgXcQ"
                className="w-full pl-9 pr-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none font-mono text-sm" />
            </div>
            <p className="mt-1 text-xs text-navy/55">Paste the full YouTube URL — we&apos;ll extract the 11-character ID automatically.</p>
          </div>
          {ytThumb && (
            <div className="rounded-xl overflow-hidden border border-cream-200">
              <img src={ytThumb} alt="" className="w-full h-auto" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-navy mb-1.5">Description</label>
            <textarea data-testid="video-description" rows={3} value={form.description} onChange={(e) => update("description", e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-cream-200 bg-cream-50 focus-gold focus:outline-none resize-y" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-navy">
              <input data-testid="video-featured" type="checkbox" checked={form.is_featured}
                onChange={(e) => update("is_featured", e.target.checked)} className="w-4 h-4 accent-[var(--gold-500)]" />
              <Star size={14} className="text-gold" /> Featured video
            </label>
            <div>
              <label className="block text-xs text-navy/60 mb-1">Display order</label>
              <input data-testid="video-order" type="number" value={form.order} onChange={(e) => update("order", parseInt(e.target.value || "0", 10))}
                className="w-full px-3 py-2 rounded-lg border border-cream-200 bg-cream-50 focus-gold focus:outline-none text-sm" />
            </div>
          </div>

          <div className="border-t border-cream-200 pt-5 flex items-center justify-between flex-wrap gap-3">
            {!isNew && (
              confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-red-700">Delete this video?</span>
                  <button type="button" onClick={del} data-testid="video-delete-confirm" className="px-3 py-1.5 rounded-full bg-red-600 text-white text-sm">Yes, delete</button>
                  <button type="button" onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 rounded-full bg-cream-100 text-navy text-sm">Cancel</button>
                </div>
              ) : (
                <button type="button" onClick={() => setConfirmDelete(true)} data-testid="video-delete"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-red-300 text-red-700 hover:bg-red-50 text-sm">
                  <Trash2 size={14} /> Delete
                </button>
              )
            )}
            <div className="flex items-center gap-3 ml-auto">
              {error && <span data-testid="video-editor-error" className="text-red-700 text-sm inline-flex items-center gap-1.5"><AlertCircle size={14} /> {error}</span>}
              <button type="button" onClick={onClose} className="btn-outline-navy px-5 py-2.5 rounded-full text-sm">Cancel</button>
              <button type="submit" disabled={saving} data-testid="video-save" className="btn-primary px-6 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60">
                <Save size={14} /> {saving ? "Saving..." : (isNew ? "Add Video" : "Save")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
